import React, { useEffect, useRef } from 'react'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import { CircularProgress, Typography, Box } from '@material-ui/core'
import moment from 'moment'

am4core.useTheme(am4themes_animated)

const GaugeChart = ({ label, data, loading, unit, min, max }) => {
  const chartRef = useRef(null)
  const chartId = `gauge-chart-${label.replace(/\s/g, '-')}`

  const extractChartData = (inputData) => {
    if (!inputData) return null
    if (inputData.data && inputData.data.graphs) {
      const metricKey = label.toLowerCase().replace(/\s/g, '')
      return inputData.data.graphs[metricKey]
    }
    return inputData
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--'
    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
  }

  useEffect(() => {
    const chartData = extractChartData(data)
    if (!chartData || loading) return

    if (chartRef.current) {
      chartRef.current.dispose()
    }

    try {
      am4core.options.autoDispose = true
      let chart = am4core.create(chartId, am4charts.GaugeChart)

      if (!chart) {
        console.error('Failed to create chart')
        return
      }

      if (chart.logo) chart.logo.disabled = true
      if (chart.startAngle) chart.startAngle = 180
      if (chart.endAngle) chart.endAngle = 360
      if (chart.innerRadius) chart.innerRadius = am4core.percent(80)
      const baseColor = am4core.color('#6d173d')

      const currentValue =
        chartData.value !== undefined
          ? chartData.value
          : chartData.values?.length
          ? chartData.values[chartData.values.length - 1].value
          : 0

      let axis = chart.xAxes.push(new am4charts.ValueAxis())
      // Use provided min/max or fall back to data min/max or defaults
      axis.min = min !== undefined ? min : (chartData.min !== null && chartData.min !== undefined ? chartData.min : 0)
      axis.max = max !== undefined ? max : (chartData.max !== null && chartData.max !== undefined ? chartData.max : 100)
      axis.strictMinMax = true

      axis.renderer.innerRadius = am4core.percent(80)
      axis.renderer.radius = am4core.percent(100)
      axis.renderer.line.strokeWidth = 0
      axis.renderer.grid.template.disabled = true
      axis.renderer.ticks.template.length = 10
      axis.renderer.ticks.template.strokeOpacity = 0.5
      axis.renderer.labels.template.disabled = true

      const segments = 10
      const range = axis.max - axis.min
      const segmentSize = range / segments

      const formatLabelValue = (value) => {
        const num = Number(value)
        const rounded = Math.round(num * 100) / 100 // Round to 2 decimal places
        return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2)
      }

      for (let i = 0; i <= segments; i++) {
        const value = axis.min + i * segmentSize
        if (i > 0 && i < segments && range < 2 && i !== Math.floor(segments / 2)) continue

        const label = axis.axisRanges.create()
        label.value = value
        label.label.text = formatLabelValue(value)
        label.label.fill = baseColor
        label.label.fontWeight = 'bold'
        label.label.fontSize = 10
        label.label.align = 'center'
        label.label.valign = 'bottom'
        label.label.radius = 25
        label.label.inside = false
        label.label.bent = true
        label.grid.disabled = true
        label.tick.disabled = true
        label.axisFill.fillOpacity = 0
      }

      chart.events.on('ready', function () {
        try {
          let tooltip = document.createElement('div')
          tooltip.style.position = 'absolute'
          tooltip.style.top = '2%'
          tooltip.style.left = '50%'
          tooltip.style.transform = 'translate(-50%, -50%)'
          tooltip.style.backgroundColor = '#6d173d'
          tooltip.style.color = 'white'
          tooltip.style.padding = '10px'
          tooltip.style.borderRadius = '4px'
          tooltip.style.fontSize = '10px'
          tooltip.style.fontFamily = 'Arial, sans-serif'
          tooltip.style.textAlign = 'center'
          tooltip.style.display = 'none'
          tooltip.style.zIndex = '999'
          tooltip.style.width = '160px'
          tooltip.style.boxShadow = '0px 2px 6px rgba(0,0,0,0.3)'

          const chartDiv = document.getElementById(chartId)
          if (chartDiv) {
            chartDiv.style.position = 'relative'
            chartDiv.appendChild(tooltip)
          }

          for (let i = 0; i < segments; i++) {
            const startValue = axis.min + i * segmentSize
            const endValue = startValue + segmentSize

            const rangeItem = axis.axisRanges.create()
            rangeItem.value = startValue
            rangeItem.endValue = endValue
            rangeItem.axisFill.fillOpacity = 1
            rangeItem.axisFill.fill = baseColor.lighten(0.1 + 0.3 * (1 - i / segments))
            rangeItem.axisFill.zIndex = -1

            const segmentDataPoints =
              chartData.values?.filter((point) => point.value >= startValue && point.value < endValue) || []

            let minSegmentTime = null
            let maxSegmentTime = null

            if (segmentDataPoints.length > 0) {
              segmentDataPoints.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
              minSegmentTime = segmentDataPoints[0].timestamp
              maxSegmentTime = segmentDataPoints[segmentDataPoints.length - 1].timestamp
            }

            rangeItem.axisFill.tooltip = false
            rangeItem.interactionsEnabled = true
            rangeItem.axisFill.interactionsEnabled = true

            rangeItem.axisFill.events.on('hit', (ev) => {
              const tooltipContent =
                `Values range: ${startValue.toFixed(1)} to ${endValue.toFixed(1)} ${chartData.unit || ''}<br>` +
                (segmentDataPoints.length === 0
                  ? `No values recorded`
                  : `Time range:<br>${formatTimestamp(minSegmentTime)} to<br>${formatTimestamp(maxSegmentTime)}`)

              tooltip.innerHTML = tooltipContent
              tooltip.style.display = 'block'
            })

            rangeItem.axisFill.events.on('over', (ev) => {
              const tooltipContent =
                `Values range: ${startValue.toFixed(1)} to ${endValue.toFixed(1)} ${chartData.unit || ''}<br>` +
                (segmentDataPoints.length === 0
                  ? `No values recorded`
                  : `Time range:<br>${formatTimestamp(minSegmentTime)} to<br>${formatTimestamp(maxSegmentTime)}`)

              tooltip.innerHTML = tooltipContent
              tooltip.style.display = 'block'
            })

            rangeItem.axisFill.events.on('out', (ev) => {
              tooltip.style.display = 'none'
            })
          }

          setTimeout(() => {
            try {
              const svgContainer = chart.svgContainer?.htmlElement
              if (svgContainer) {
                svgContainer.querySelectorAll('g[opacity="0.4"][aria-labelledby]').forEach((el) => {
                  const title = el.querySelector('title')
                  if (title && title.textContent.includes('Chart created using amCharts library')) {
                    el.remove()
                  }
                })
              }
            } catch (error) {
              console.warn('Error removing amCharts branding:', error)
            }
          }, 100)
        } catch (error) {
          console.error('Error in chart ready event:', error)
        }
      })

      let hand = chart.hands.push(new am4charts.ClockHand())
      hand.value = currentValue
      hand.pin.disabled = false
      hand.pin.radius = 8
      hand.pin.fill = baseColor
      hand.pin.stroke = am4core.color('#ffffff')
      hand.fill = baseColor
      hand.stroke = baseColor
      hand.strokeWidth = 3

      chart.paddingTop = 30
      chart.paddingBottom = 0
      chart.paddingLeft = 15
      chart.paddingRight = 15

      chartRef.current = chart

      return () => {
        if (chart) {
          chart.dispose()
        }
      }
    } catch (error) {
      console.error('Error creating gauge chart:', error)
    }
  }, [data, loading, chartId, label])

  const chartData = extractChartData(data)

  return (
    <Box 
      display='flex' 
      flexDirection='column' 
      alignItems='center' 
      mb={1}
      width="100%"
      minHeight="200px"
      p={0.5}
    >
      <div
        id={chartId}
        style={{
          width: '100%',
          height: 160,
          position: 'relative',
          zIndex: 10,
          overflow: 'visible',
        }}
      />
      {loading && <CircularProgress size={24} />}
      <Box mt={1} textAlign='center'>
        <Typography
          variant='h6'
          style={{
            color: '#6d173d',
            fontWeight: 'bold',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {chartData?.value ?? 0} {unit || chartData?.unit}
        </Typography>
      </Box>
      <Box mt={1} textAlign='center'>
        <Typography variant='caption' color='textSecondary'>
          Min: {chartData?.min ?? '--'} {unit || (chartData?.unit ?? '')}
          {chartData?.minTimestamp ? ` (${formatTimestamp(chartData.minTimestamp)})` : ''}
          <br />
          Max: {chartData?.max ?? '--'} {unit || (chartData?.unit ?? '')}
          {chartData?.maxTimestamp ? ` (${formatTimestamp(chartData.maxTimestamp)})` : ''}
        </Typography>
      </Box>
    </Box>
  )
}

export default GaugeChart
